import { css } from 'styled-components';
import { ITheme, IThemePalette } from '../../Themes/interfaces';
import { baseFontCss, inputFocused } from '../../common/mixins';
import { Icon } from '../../Components/Icons/Icon';
import { ISelectStyleProps } from './selectTypes';
import { transparentize } from 'polished';

interface ISelectStyleOverrides extends ISelectStyleProps {
  theme: ITheme;
}

export const selectColours = (palette: IThemePalette) => ({
  select: {
    input: {
      text: palette.primary.text[100],
      focus: {
        background: palette.primary.second[25],
        border: palette.primary.second[25]
      },
      hover: {
        background: palette.primary.second[25],
        border: palette.primary.second[25]
      },
      invalid: {
        text: palette.alerts.alert,
        border: palette.alerts.alert
      },
      border: palette.primary.text[20]
    },
    indicator: {
      text: palette.primary.text[100]
    },
    optionMenu: {
      border: palette.primary.text[20],
      boxShadow: transparentize(0.9, palette.primary.text[75])
    },
    option: {
      hover: {
        text: palette.primary.text[100],
        background: palette.primary.second[25],
        border: palette.primary.second[25]
      },
      focus: {
        text: palette.primary.text[100],
        background: palette.primary.second[25],
        border: palette.primary.second[25]
      },
      background: palette.neutral.white
    },
    selectedOption: {
      text: palette.primary.text[100]
    }
  }
});

export const selectStyles = css`
  ${baseFontCss};

  .react-select__control {
    height: 3rem;
    width: 100%;
    font-size: 1rem;
    color: ${({ theme }) => theme.colours.select.input.text};
    border: ${({ theme, bare }) => (bare ? 'none' : `1px solid ${theme.colours.select.input.border}`)};
    border-radius: 0.25rem;
    appearance: none;
    margin-top: 0.5rem;
    cursor: pointer;
    box-shadow: none;
    ${({ invalid, theme }: ISelectStyleOverrides) =>
      invalid &&
      css`
        border: 1px solid ${theme.colours.select.input.invalid.border};
      `}
    &.react-select__control--menu-is-open {
      border-bottom: none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
    &.react-select__control--is-focused {
      ${inputFocused};
    }
  }
  .react-select__placeholder,
  .react-select__single-value {
    ${({ textAlign = 'left' }) =>
      css`
        text-align: ${textAlign};
      `}
  }
  .react-select__value-container {
    line-height: ${({ theme }) => theme.text.lineHeight};
    ${({ justifyContent }: ISelectStyleOverrides) =>
      justifyContent &&
      css`
        justify-content: ${justifyContent};
      `}
  }
  .react-select__control:hover {
    border: ${({ theme }) => theme.colours.select.input.hover.border};
    background-color: ${({ theme }) => theme.colours.select.input.hover.background};
  }
  .react-select__option {
    background-color: ${({ theme }) => theme.colours.select.option.background};
  }
  .react-select__option:hover {
    background-color: ${({ theme }) => theme.colours.select.option.hover.background};
    color: ${({ theme }) => theme.colours.select.option.hover.text};
  }
  .react-select__option:active,
  .react-select__option--is-focused,
  .react-select__option:focus {
    background-color: ${({ theme }) => theme.colours.select.option.focus.background};
    color: ${({ theme }) => theme.colours.select.option.focus.text};
  }
  .react-select__option--is-selected {
    color: ${({ theme }) => theme.colours.select.selectedOption.text};
  }
  .react-select__dropdown-indicator {
    font-size: 1.5rem;
    color: ${({ theme }) => theme.colours.select.indicator.text};
    transition: all 50ms ease;
  }
  .react-select__control--menu-is-open {
    .react-select__dropdown-indicator {
      ${({ animateDropdownIcon }) =>
        animateDropdownIcon &&
        css`
          transform: rotate(180deg);
        `}
    }
  }
  .react-select__indicator-separator {
    display: none;
  }
  .react-select__dropdown-indicator:hover {
    color: ${({ theme }) => theme.colours.select.indicator.text};
  }
  .react-select__menu {
    margin-top: 0;
    border-radius: 0 0 0.25rem 0.25rem;
    border: 1px solid ${({ theme }) => theme.colours.select.optionMenu.border};
    border-top-width: 0;
    box-shadow: 0 0.125rem 0.25rem ${({ theme }) => theme.colours.select.optionMenu.boxShadow};
    text-align: left;
  }
  .react-select__menu-list {
    padding: 0.25rem 0.125rem;
  }
  .react-select__group-heading {
    padding: 0.75rem;
    font-size: 1rem;
    text-align: center;
  }
  ${Icon} {
    padding: 0;
  }
`;
